/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import FocusTrap from 'focus-trap-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Popup } from 'semantic-ui-react';

import useElementVisible from 'hooks/useElementVisible';
import { isNonMobile } from 'utils/responsive';

import { BdInteractiveSpan } from 'components/_common/Accessibility/BdInteractiveSpan';
import { Typography } from 'components/_common/Typography';

import { IconButton } from '../IconButton';

export type BdPosition =
  | 'top left'
  | 'top right'
  | 'bottom right'
  | 'bottom left'
  | 'right center'
  | 'left center'
  | 'top center'
  | 'bottom center';

export type BdTooltipBoundaries = {
  right: number;
  left: number;
};

export type BdTooltipOffset = {
  bottom: string;
  right: string;
  left: string;
};

type BdTooltipProps = Partial<{
  text: string | React.ReactNode;
  customPopup?: React.ReactNode;
  disabled: boolean;
  wide: boolean | 'very';
  fixedWidth?: string;
  /** Position of the tooltip notch */
  position?: BdPosition;
  className: string;
  /** The initial offset of the tooltip (unchanged onless dynamicPositioning is true) */
  offset: string;
  popperModifiers: object;
  hideElementOffsets?: { top: number; bottom: number };
  stopClickPropagation?: boolean;
  /** When this is true, noSelfClickClose should also be true or it will break */
  displayOnlyOnClick?: boolean;
  persistOnScroll?: boolean;
  persistOnBlur?: boolean;
  persistOnMouseout?: boolean;
  noSelfClickClose?: boolean;
  /** Dynamically positions the tooltip */
  dynamicPositioning?: boolean;
  /** An optional element which the tooltip should appear from */
  hoverContext?: React.MutableRefObject<any>;
  /** The window boundries that will cause the tooltip to change when dynamicPositioning is true */
  tooltipBoundaries?: BdTooltipBoundaries;
  /** How far to offset the tooltips if dynamic positioning is true */
  dynamicTooltipOffset?: BdTooltipOffset;
  DEBUG?: boolean;
}>;

const BdTooltip: React.FC<BdTooltipProps> = ({
  text,
  customPopup,
  disabled = false,
  wide,
  fixedWidth,
  position = 'top center',
  className,
  offset,
  popperModifiers = { preventOverflow: { boundariesElement: 'window' } },
  hideElementOffsets = { top: 0, bottom: 0 },
  stopClickPropagation = false,
  DEBUG = false,
  displayOnlyOnClick = false,
  persistOnScroll = false,
  persistOnBlur = false,
  persistOnMouseout = false,
  noSelfClickClose = false,
  children,
  dynamicPositioning = false,
  hoverContext = null,
  tooltipBoundaries = { left: 0, right: window.innerWidth },
  dynamicTooltipOffset = { bottom: '0', right: '-50% + 13px', left: '+50% - 13px' },
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(position);
  const [tooltipOffset, setTooltipOffset] = useState(offset);
  const [shouldReturnFocus, setShouldReturnFocus] = useState(persistOnScroll);
  const isNonMobileDevice = isNonMobile(useBreakpointIndex());
  const [isTriggerVisible, triggerRef] = useElementVisible<HTMLDivElement>({
    offsetTop: hideElementOffsets.top,
    offsetBottom: hideElementOffsets.bottom,
    enableScrollListener: isOpen,
  });

  function getTooltipPos(): BdPosition {
    const refContext = triggerRef;

    if (!refContext?.current?.getBoundingClientRect()) return 'bottom center';

    if (
      refContext?.current?.getBoundingClientRect()?.right > tooltipBoundaries.right &&
      refContext?.current?.getBoundingClientRect()?.left < tooltipBoundaries.left
    ) {
      return 'bottom center';
    }

    if (refContext?.current?.getBoundingClientRect()?.right > tooltipBoundaries.right) {
      return 'bottom right';
    }
    if (refContext?.current?.getBoundingClientRect()?.left < tooltipBoundaries.left) {
      return 'bottom left';
    }
    return 'bottom center';
  }

  function getTooltipOffset() {
    const refContext = triggerRef;

    if (!refContext?.current?.getBoundingClientRect()) return dynamicTooltipOffset.bottom;

    if (
      refContext?.current?.getBoundingClientRect()?.right > tooltipBoundaries.right &&
      refContext?.current?.getBoundingClientRect()?.left < tooltipBoundaries.left
    ) {
      return dynamicTooltipOffset.bottom;
    }

    if (refContext?.current?.getBoundingClientRect()?.right > tooltipBoundaries.right) {
      return dynamicTooltipOffset.right;
    }
    if (refContext?.current?.getBoundingClientRect()?.left < tooltipBoundaries.left) {
      return dynamicTooltipOffset.left;
    }

    return dynamicTooltipOffset.bottom;
  }

  const closePopup = useCallback(
    (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!isOpen) return;
      setIsOpen(false);
      stopClickPropagation && e && e.nativeEvent && e.stopPropagation && e.stopPropagation();
    },
    [isOpen]
  );

  /** Hides the tooltip if the trigger is hidden via css */
  const handleResize = useCallback(() => {
    if (isOpen && !triggerRef?.current?.getClientRects()?.length) closePopup();
  }, [isOpen, closePopup]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (persistOnScroll && !isTriggerVisible && isOpen) closePopup();
  }, [isTriggerVisible]);

  const onOpen = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    stopClickPropagation && e && e.stopPropagation && e.stopPropagation();
    if (dynamicPositioning) {
      setTooltipOffset(getTooltipOffset());
      setTooltipPos(getTooltipPos());
    }

    /*
     * For accessibility it should return focus to the element if being opened by
     * keyboard so the tab targetting doesn't reset
     */
    setShouldReturnFocus(persistOnScroll && e.nativeEvent instanceof KeyboardEvent);
    setIsOpen(true);
  };

  const tooltipContent = (
    <div tabIndex={-1} id="tooltip-content-container" sx={{ outline: 'none' }}>
      {noSelfClickClose && (
        <IconButton
          ariaLabel="Close tooltip"
          icon="icon-close"
          size="medium"
          onClick={closePopup}
          invert
          sx={{
            display: 'inline',
            fontSize: '16px',
            lineHeight: '16px',
            verticalAlign: 'middle',
            cursor: 'pointer',
            position: 'absolute',
            top: '12px',
            right: '12px',
          }}
        />
      )}
      {customPopup ||
        (text && (
          <Typography variant="caption" color="stone10">
            {text}
          </Typography>
        ))}
    </div>
  );

  return (
    <Popup
      // TODO: 68382fe1: many props are duplicate for these popup controls.
      pinned={DEBUG}
      context={hoverContext ?? triggerRef}
      wide={wide}
      open={isOpen || DEBUG}
      className={className}
      onOpen={onOpen}
      onClose={closePopup}
      onClick={e => (!noSelfClickClose ? closePopup(e) : null)}
      position={tooltipPos}
      trigger={
        displayOnlyOnClick ? (
          <BdInteractiveSpan data-testid="trigger-content" ref={triggerRef}>
            {children}
          </BdInteractiveSpan>
        ) : (
          <span data-testid="trigger-content" className={className} ref={triggerRef}>
            {children}
          </span>
        )
      }
      openOnTriggerClick
      disabled={disabled}
      closeOnDocumentClick
      closeOnEscape
      hideOnScroll={!persistOnScroll}
      closeOnTriggerBlur={!persistOnBlur}
      closeOnTriggerClick={!persistOnBlur}
      closeOnTriggerMouseLeave={!persistOnMouseout}
      closeOnPortalMouseLeave={!persistOnMouseout}
      on={isNonMobileDevice && !displayOnlyOnClick ? ['hover', 'focus', 'click'] : ['click']}
      popperModifiers={popperModifiers}
      offset={tooltipOffset}
      sx={{
        ...(fixedWidth?.length && {
          '&.ui.popup': { width: fixedWidth, maxWidth: fixedWidth, minWidth: fixedWidth },
        }),
      }}
      {...props}
    >
      <Popup.Content sx={{ minHeight: '100%' }}>
        {displayOnlyOnClick ? (
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: true,
              initialFocus: '#tooltip-content-container',
              returnFocusOnDeactivate: shouldReturnFocus,
            }}
          >
            {tooltipContent}
          </FocusTrap>
        ) : (
          tooltipContent
        )}
      </Popup.Content>
    </Popup>
  );
};

export default BdTooltip;
